/** @flow */

import { takeLatest, put, all, call } from "redux-saga/effects";
import http from "@http";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import type { Action } from "redux-action";
import {
  GetReportingFocusListsEmployeeReport,
  GetFocusListsEmployeeReportSchema,
  CreateFocusListsEmployeeSavedReport,
  UpdateFocusListsEmployeeSavedReport,
  DeleteFocusListsEmployeeSavedReport,
  ExportFocusListsEmployeeReport,
  ExportFocusListsEmployeeReportSuccess,
} from "@modules/reporting/constants/actions";
import actionCreator from "@modules/reporting/actionCreator";
import type { EmployeeReport } from "@modules/reporting/reducer/focus-lists/employee/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import actionCreators from "@modules/reporting/actionCreator";
import { downloadCsvFile } from "@sagas/utils";
import { parseSchema } from "@utils/schema";

type ReportDataRequest = $Exact<{
  clientId: number,
  report: EmployeeReport,
  noRedirect?: boolean,
}>;

function* onGetReportingFocusListsEmployeeReport({
  payload,
}: Action<ReportDataRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.report) {
    throw Error("Invalid payload provided");
  }

  try {
    const [reportResponse, chartsResponse] = yield all([
      call(
        http.post,
        `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/employee`,
        payload.report.filters,
      ),
      call(
        http.post,
        `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/employee/charts`,
        payload.report.filters,
      ),
    ]);

    yield put(
      actionCreator.GetReportingFocusListsEmployeeReportSuccess({
        details: payload.report,
        reportData: reportResponse.data.content,
        percentageOfAbsence: chartsResponse.data.percentageOfAbsence,
        top10: chartsResponse.data.top10,
        percentageOfEmployees: chartsResponse.data.percentageOfEmployees,
      }),
    );

    if (!payload.noRedirect) {
      yield put(
        push(route("REPORTING_FOCUS_LISTS_EMPLOYEE_REPORT", { clientId: payload.clientId })),
      );
    }
  } catch (e) {
    yield put(
      actionCreator.GetReportingFocusListsEmployeeReportError({
        error: e.message,
      }),
    );
  }
}

type ReportSchemaRequest = $Exact<{
  clientId: number,
}>;

export function* onGetFocusListsEmployeeReportSchema({
  payload,
}: Action<ReportSchemaRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error("Invalid payload provided");
  }

  try {
    const schema = yield parseSchema("EmployeeFocusListReportFiltersDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetFocusListsEmployeeReportSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetFocusListsEmployeeReportSchemaError(e.response));
  }
}

type CreateFocusListsEmployeeSavedReportRequest = $Exact<{
  clientId: number,
  filters: Object,
  name: string,
}>;

export function* onCreateFocusListsEmployeeSavedReport({
  payload,
}: Action<CreateFocusListsEmployeeSavedReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters || !payload.name) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/employee/my-saved-reports`,
      {
        name: payload.name,
        filters: payload.filters,
      },
    );

    yield all([
      put(actionCreators.CreateFocusListsEmployeeSavedReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetReportingFocusListsEmployeeMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("CALLBACK_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateFocusListsEmployeeSavedReportError(e.response));
  }
}

type UpdateFocusListsEmployeeSavedReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  data: Object,
  shouldFetchReportData?: boolean,
}>;

export function* onUpdateFocusListsEmployeeSavedReport({
  payload,
}: Action<UpdateFocusListsEmployeeSavedReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.reportId || !payload.data) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/employee/my-saved-reports/${payload.reportId}`,
      payload.data,
    );

    const additionalActions = payload.shouldFetchReportData
      ? [
          put(
            actionCreators.GetReportingFocusListsEmployeeReport({
              clientId: payload.clientId,
              report: response.data,
              noRedirect: true,
            }),
          ),
        ]
      : [];

    yield all([
      put(actionCreators.UpdateFocusListsEmployeeSavedReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetReportingFocusListsEmployeeMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_UPDATED")),
      ...additionalActions,
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateFocusListsEmployeeSavedReportError(e.response));
  }
}

type DeleteFocusListsEmployeeSavedReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  successMessage: string,
}>;

export function* onDeleteFocusListsEmployeeSavedReport({
  payload,
}: Action<DeleteFocusListsEmployeeSavedReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.reportId || !payload.successMessage) {
    throw Error("Invalid payload provided");
  }

  try {
    yield call(
      http.delete,
      `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/employee/my-saved-reports/${payload.reportId}`,
    );

    yield all([
      put(actionCreators.DeleteFocusListsEmployeeSavedReportSuccess()),
      put(actionCreators.GetReportingFocusListsEmployeeMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteFocusListsEmployeeSavedReportError(e.response));
  }
}

export function* onExportFocusListsEmployeeReport({
  payload,
}: Action<ReportDataRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.report) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/employee/export`,
      payload.report.filters,
      {
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield put(actionCreators.ExportFocusListsEmployeeReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportFocusListsEmployeeReportError(e.response));
  }
}

export function* onExportFocusListsEmployeeReportSuccess({
  payload,
}: Action<any>): Generator<*, void, any> {
  yield downloadCsvFile(payload, "focus-lists-employee-report.csv");
}

export default function* reportSaga(): Generator<*, void, void> {
  yield all([
    takeLatest(GetReportingFocusListsEmployeeReport, onGetReportingFocusListsEmployeeReport),
    takeLatest(GetFocusListsEmployeeReportSchema, onGetFocusListsEmployeeReportSchema),
    takeLatest(CreateFocusListsEmployeeSavedReport, onCreateFocusListsEmployeeSavedReport),
    takeLatest(UpdateFocusListsEmployeeSavedReport, onUpdateFocusListsEmployeeSavedReport),
    takeLatest(DeleteFocusListsEmployeeSavedReport, onDeleteFocusListsEmployeeSavedReport),
    takeLatest(ExportFocusListsEmployeeReport, onExportFocusListsEmployeeReport),
    takeLatest(ExportFocusListsEmployeeReportSuccess, onExportFocusListsEmployeeReportSuccess),
  ]);
}
